export function useSurfaceEditorUtils() {

  return {
    fillToStyle({fill, patternOffset = 0, isButton}) {
      switch(fill.type) {
        case SURFACE_EDITOR_FILL_TYPES.solid:
          return {
            background: fill.color
          };
        case SURFACE_EDITOR_FILL_TYPES.linearGradient:
          return {
            background: `linear-gradient(${fill.angle || 0}deg, ${fill.stops.join(', ')})`
          };
        case SURFACE_EDITOR_FILL_TYPES.imagePattern:
          return {
            background: `url(${isButton ? fill.btnUrl : fill.url}) -${patternOffset}px -${patternOffset}px / contain repeat`,
            objectFit: 'cover'
          };
      }
    },

    findElementInSurface({elementId, surfaces}) {
      for (let i = 0; i < surfaces.length; i++) {
        const surface = surfaces[i];

        if (surface.background?.id === elementId) {
          return {
            surface: surface,
            element: surface.background
          };
        }

        const elementIndex = surface.elements.findIndex(e => e.id === elementId);

        if (elementIndex > -1) {
          return {
            surface,
            element: surface.elements[elementIndex],
            elementIndex
          };
        }
      }

      return null;
    },

    getOverlappingElements({element, surface}) {
      const isOverlapping = aElement => {
        const rectIntersect = {
          left: Math.max(element.x, aElement.x),
          right: Math.min(element.x + element.width, aElement.x + aElement.width),
          bottom: Math.min(element.y + element.height, aElement.y + aElement.height),
          top: Math.max(element.y, aElement.y)
        };

        const intersectBase = Math.max((rectIntersect.right - rectIntersect.left), 0);
        const intersectHeight = Math.max((rectIntersect.bottom - rectIntersect.top), 0);

        return Boolean(intersectBase * intersectHeight);
      }

      return surface.elements.reduce((collection, aElement, index) => {
        if (isOverlapping(aElement)) {
          collection.push({
            index,
            element: aElement
          });
        }

        return collection;
      }, []);
    },

    centerElementInSurface({element: {width: baseWidth, height: baseHeight}, surface, minDistanceFromEdge = 0, minDimLength = 200}) {

      const maxWidth = surface.width - (minDistanceFromEdge * 2);
      const maxHeight = surface.height - (minDistanceFromEdge * 2);

      const maxScale = Math.min(
        maxWidth / baseWidth,
        maxHeight / baseHeight
      );

      // Scale needed to satisfy minDimLength (SOFT CONSTRAINT)
      const minDimScale =
        minDimLength
          ? minDimLength / Math.min(baseWidth, baseHeight)
          : 1;

      // Final scale
      const scale = Math.min(
        maxScale,
        Math.max(1, Math.min(minDimScale, maxScale))
      );

      const width = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(baseWidth * scale));
      const height = Math.max(SURFACE_EDITOR_MIN_ELEMENT_DIMENSION, Math.round(baseHeight * scale));

      const x = Math.round(minDistanceFromEdge + ((maxWidth - width) / 2));
      const y = Math.round(minDistanceFromEdge + ((maxHeight - height) / 2));

      return {
        x,
        y,
        width,
        height,
        scale
      };
    },

    /**
     * Converts pixels to screen (user) points based on provided print dots per inch density
     */
    fontPixelsToPoints({pixels}) {
      return pixels * 0.75;
    },

    /**
     * Converts screen (user) points to pixels based on provided print dots per inch density
     */
    fontPointsToPixels({points}) {
      return points / 0.75;
    },

    /**
     * Converts  0-100 rounding value to actual pixel radius for images and rectangles
     * 0 = 0px, 100 = min(width, height) / 2
     */
    getRoundingInPixels({rounding, elementWidth, elementHeight}) {
      const maxRadius = Math.min(elementWidth, elementHeight) / 2;
      return (rounding / 100) * maxRadius;
    },

    /**
     * Gets the axis-aligned bounding box (AABB) for a rotated element
     * Used for positioning UI elements and calculating snapping guides
     */
    getRotatedBoundingBox({x, y, width, height, rotation}) {
      if (!rotation || rotation === 0) {
        return {x, y, width, height};
      }

      const centerX = x + width / 2;
      const centerY = y + height / 2;
      const angleRad = rotation * (Math.PI / 180);
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);

      // Calculate rotated corners
      const halfW = width / 2;
      const halfH = height / 2;

      const corners = [
        {x: -halfW, y: -halfH},
        {x: halfW, y: -halfH},
        {x: -halfW, y: halfH},
        {x: halfW, y: halfH}
      ].map(corner => ({
        x: centerX + corner.x * cos - corner.y * sin,
        y: centerY + corner.x * sin + corner.y * cos
      }));

      // Find AABB
      const minX = Math.min(...corners.map(c => c.x));
      const maxX = Math.max(...corners.map(c => c.x));
      const minY = Math.min(...corners.map(c => c.y));
      const maxY = Math.max(...corners.map(c => c.y));

      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
      };
    }
  };
}
